import React, { useState, useEffect, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Container, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel, Chip, CircularProgress, Tabs, Tab, Autocomplete, Avatar } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import TopThreeFisherman from './TopThreeFisherman';
import { createFilterOptions } from '@mui/material/Autocomplete';

const useStyles = createUseStyles({
  container: { marginTop: '20px' },
  title: { marginBottom: '20px' },
  formControl: { minWidth: 120, marginBottom: '20px', width: '100%' },
  addButtonContainer: { display: 'flex', gap: '10px', marginBottom: '20px' },
  dataGrid: { height: 400, width: '100%' },
  tabs: { marginBottom: '20px' },
});

const BASE_URL = 'https://sandpaperdashboard.com/navarrefishingrodeo/api/';
const ENDPOINTS = {
  fish: 'fish/',
  sponsors: 'sponsors/',
  years: 'years/',
  divisions: 'divisions/',
  fishermen: 'fishermen/',
  leaderboards: 'leaderboards/',
  fishevents: 'fishevents/',
  leaderboardResults: (divisionId) => `leaderboard-results/${divisionId}/`,
  topThreeFishermen: (divisionId) => `divisions/${divisionId}/top_three/`,
};

const NFRLeaderboard = () => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [leaderboardResults, setLeaderboardResults] = useState([]);
  const [fishEvents, setFishEvents] = useState([]);
  const [fish, setFish] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [fishermen, setFishermen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('');
  const [currentItem, setCurrentItem] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [topThreeModalOpen, setTopThreeModalOpen] = useState(false);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);
  const [fishermanDialogOpen, setFishermanDialogOpen] = useState(false);
  const [newFisherman, setNewFisherman] = useState({ firstname: '', lastname: '' });
  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => `${option.firstname} ${option.lastname}`,
  });
  const [divisionFish, setDivisionFish] = useState([]);
  const [divisionsWithSponsors, setDivisionsWithSponsors] = useState([]);

  useEffect(() => {
    fetchDivisions();
    fetchFish();
    fetchSponsors();
    fetchFishermen();
  }, []);

  useEffect(() => {
    if (selectedDivision) {
      fetchLeaderboardResults(selectedDivision.id);
      fetchFishEvents(selectedDivision.id);
      fetchDivisionFish(selectedDivision.id);
    }
  }, [selectedDivision]);

  const fetchDivisions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}${ENDPOINTS.divisions}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      
      // Fetch sponsors for each division
      const divisionsWithSponsorsData = await Promise.all(data.map(async (division) => {
        const sponsorsResponse = await fetch(`${BASE_URL}${ENDPOINTS.divisions}${division.id}/sponsors/`);
        if (!sponsorsResponse.ok) throw new Error(`HTTP error! status: ${sponsorsResponse.status}`);
        const sponsorsData = await sponsorsResponse.json();
        return { ...division, sponsors: sponsorsData };
      }));

      setDivisionsWithSponsors(divisionsWithSponsorsData);
      if (divisionsWithSponsorsData.length > 0) setSelectedDivision(divisionsWithSponsorsData[0]);
    } catch (error) {
      console.error('Error fetching divisions:', error);
      showSnackbar('Error fetching divisions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboardResults = async (divisionId) => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}${ENDPOINTS.leaderboardResults(divisionId)}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setLeaderboardResults(data.leaderboards);
    } catch (error) {
      console.error('Error fetching leaderboard results:', error);
      showSnackbar('Error fetching leaderboard results', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchFishEvents = async (divisionId) => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}${ENDPOINTS.fishevents}?division=${divisionId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setFishEvents(data);
    } catch (error) {
      console.error('Error fetching fish events:', error);
      showSnackbar('Error fetching fish events', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchFish = async () => {
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.fish}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setFish(data);
    } catch (error) {
      console.error('Error fetching fish:', error);
      showSnackbar('Error fetching fish', 'error');
    }
  };

  const fetchSponsors = async () => {
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.sponsors}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setSponsors(data);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
      showSnackbar('Error fetching sponsors', 'error');
    }
  };

  const fetchFishermen = async () => {
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.fishermen}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setFishermen(data);
    } catch (error) {
      console.error('Error fetching fishermen:', error);
      showSnackbar('Error fetching fishermen', 'error');
    }
  };

  const fetchDivisionFish = async (divisionId) => {
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.fish}?division=${divisionId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setDivisionFish(data);
    } catch (error) {
      console.error('Error fetching division fish:', error);
      showSnackbar('Error fetching division fish', 'error');
    }
  };

  const handleAdd = (type) => {
    setDialogMode('add');
    setCurrentItem(type === 'leaderboard' ? { name: '', sponsors: [], fish: [] } : { time_date: '', weight: '', fish: null, fisherman: null, leaderboard: null });
    setOpenDialog(true);
  };

  const handleEdit = (item, type) => {
    setDialogMode('edit');
    setCurrentItem({ ...item });
    setOpenDialog(true);
  };

  const handleDelete = async (id, type) => {
    try {
      const endpoint = type === 'leaderboard' ? ENDPOINTS.leaderboards : ENDPOINTS.fishevents;
      const response = await fetch(`${BASE_URL}${endpoint}${id}/`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      if (type === 'leaderboard') {
        fetchLeaderboardResults(selectedDivision.id);
      } else {
        fetchFishEvents(selectedDivision.id);
      }
      showSnackbar(`${type} deleted successfully`, 'success');
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      showSnackbar(`Error deleting ${type}`, 'error');
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDialogSave = async () => {
    try {
      const method = dialogMode === 'add' ? 'POST' : 'PUT';
      const endpoint = tabValue === 0 ? ENDPOINTS.leaderboards : ENDPOINTS.fishevents;
      const url = dialogMode === 'add' ? `${BASE_URL}${endpoint}` : `${BASE_URL}${endpoint}${currentItem.id}/`;
      let dataToSend;
      if (tabValue === 0) {
        dataToSend = {
          ...currentItem,
          sponsors: Array.isArray(currentItem.sponsors) ? currentItem.sponsors : [],
          fish: Array.isArray(currentItem.fish) ? currentItem.fish : [],
          division: selectedDivision.id,
        };
      } else {
        dataToSend = {
          ...currentItem,
          time_date: currentItem.time_date || null,
          weight: parseFloat(currentItem.weight) || 0,
          fish: currentItem.fish ? [currentItem.fish] : [], // Send as a list
          fisherman: currentItem.fisherman ? [currentItem.fisherman] : [], // Send as a list
          leaderboard: currentItem.leaderboard ? [currentItem.leaderboard] : [], // Send as a list
          division: selectedDivision.id,
        };
      }
      console.log('Sending data:', dataToSend);
      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server response:', errorData);
        throw new Error(`HTTP error! status: ${response.status}, message: ${JSON.stringify(errorData)}`);
      }
      const responseData = await response.json();
      console.log('Server response:', responseData);
      if (tabValue === 0) {
        fetchLeaderboardResults(selectedDivision.id);
      } else {
        fetchFishEvents(selectedDivision.id);
      }
      setOpenDialog(false);
      showSnackbar(`${tabValue === 0 ? 'Leaderboard' : 'Fish Event'} ${dialogMode === 'add' ? 'added' : 'updated'} successfully`, 'success');
    } catch (error) {
      console.error('Error saving item:', error);
      showSnackbar(`Error ${dialogMode === 'add' ? 'adding' : 'updating'} item: ${error.message}`, 'error');
    }
  };

  const handleAddFisherman = () => {
    setFishermanDialogOpen(true);
  };

  const handleFishermanDialogClose = () => {
    setFishermanDialogOpen(false);
    setNewFisherman({ firstname: '', lastname: '' });
  };

  const handleFishermanDialogSave = async () => {
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.fishermen}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFisherman),
      });
      if (!response.ok) throw new Error('Network response was not ok');
      await fetchFishermen();
      setFishermanDialogOpen(false);
      showSnackbar('Fisherman added successfully', 'success');
    } catch (error) {
      console.error('Error adding fisherman:', error);
      showSnackbar('Error adding fisherman', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLeaderboardNameClick = (leaderboard) => {
    setSelectedLeaderboard(leaderboard);
    setTopThreeModalOpen(true);
  };

  const handleTopThreeModalClose = () => {
    setTopThreeModalOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const leaderboardColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Name', width: 130, renderCell: (params) => (
      <Typography style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleLeaderboardNameClick(params.row)}>
        {params.value}
      </Typography>
    )},
    { field: 'sponsors', headerName: 'Sponsors', width: 200, renderCell: (params) => (
      <Box>
        {params.value && params.value.map((sponsor) => (
          <Chip key={sponsor.id} label={sponsor.name} size="small" style={{ margin: '2px' }} />
        ))}
      </Box>
    )},
    { field: 'fish', headerName: 'Fish', width: 200, renderCell: (params) => (
      <Box>
        {params.value && params.value.map((fish) => (
          <Chip key={fish.id} label={fish.name} size="small" style={{ margin: '2px' }} />
        ))}
      </Box>
    )},
    { field: 'actions', headerName: 'Actions', width: 130, renderCell: (params) => (
      <Box>
        <Button onClick={() => handleEdit(params.row, 'leaderboard')}><EditIcon /></Button>
        <Button onClick={() => handleDelete(params.row.id, 'leaderboard')}><DeleteIcon /></Button>
      </Box>
    )},
  ];

  const fishEventColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'time_date', headerName: 'Date & Time', width: 200 },
    { field: 'weight', headerName: 'Weight', width: 100 },
    { field: 'fish', headerName: 'Fish', width: 130, valueGetter: (params) => params.value?.name || 'N/A' },
    { field: 'fisherman', headerName: 'Fisherman', width: 200, valueGetter: (params) => params.value ? `${params.value.firstname} ${params.value.lastname}` : 'N/A' },
    { field: 'actions', headerName: 'Actions', width: 130, renderCell: (params) => (
      <Box>
        <Button onClick={() => handleEdit(params.row, 'fishevent')}><EditIcon /></Button>
        <Button onClick={() => handleDelete(params.row.id, 'fishevent')}><DeleteIcon /></Button>
      </Box>
    )},
  ];
  const handleAddNewFisherman = async (inputValue) => {
    const [firstname, ...lastnameParts] = inputValue.split(' ');
    const lastname = lastnameParts.join(' ');
    const newFisherman = { firstname, lastname };

    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.fishermen}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFisherman),
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const addedFisherman = await response.json();
      
      // Update the fishermen list
      setFishermen([...fishermen, addedFisherman]);
      
      // Update the current item with the new fisherman
      setCurrentItem({ ...currentItem, fisherman: addedFisherman.id });
      
      showSnackbar('New fisherman added successfully', 'success');
    }
     catch (error) {
      console.error('Error adding new fisherman:', error);
      showSnackbar('Error adding new fisherman', 'error');
    }
  };

  return (
    <Container className={classes.container}>
      <Typography variant="h4" component="h1" className={classes.title}>
        Navarre Fishing Rodeo Dashboard
      </Typography>
      <FormControl fullWidth className={classes.formControl}>
        <InputLabel>Division</InputLabel>
        <Select
          value={selectedDivision?.id || ''}
          onChange={(e) => setSelectedDivision(divisionsWithSponsors.find(d => d.id === e.target.value))}
        >
          {divisionsWithSponsors.map((division) => (
            <MenuItem key={division.id} value={division.id}>
              <Box display="flex" alignItems="center">
                <Typography>{division.name}</Typography>
                {division.sponsors && division.sponsors.length > 0 && (
                  <Box ml={2} display="flex" alignItems="center">
                    {division.sponsors.map((sponsor) => (
                      <Box key={sponsor.id} mr={1} display="flex" alignItems="center">
                        {sponsor.logo && (
                          <Avatar
                            src={sponsor.logo}
                            alt={sponsor.name}
                            sx={{ width: 24, height: 24, mr: 0.5 }}
                          />
                        )}
                        <Typography variant="caption">{sponsor.name}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tabs value={tabValue} onChange={handleTabChange} className={classes.tabs}>
        <Tab label="Leaderboards" />
        <Tab label="Fish Events" />
      </Tabs>
      <div className={classes.addButtonContainer}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddFisherman}
        >
          Add Fisherman
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAdd(tabValue === 0 ? 'leaderboard' : 'fishevent')}
        >
          Add {tabValue === 0 ? 'Leaderboard' : 'Fish Event'}
        </Button>
      </div>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={tabValue === 0 ? leaderboardResults : fishEvents}
          columns={tabValue === 0 ? leaderboardColumns : fishEventColumns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          className={classes.dataGrid}
        />
      )}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>{dialogMode === 'add' ? 'Add' : 'Edit'} {tabValue === 0 ? 'Leaderboard' : 'Fish Event'}</DialogTitle>
        <DialogContent>
          {tabValue === 0 ? (
            // Leaderboard form
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                fullWidth
                value={currentItem.name || ''}
                onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
              />
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel>Sponsors</InputLabel>
                <Select
                  multiple
                  value={currentItem.sponsors || []}
                  onChange={(e) => setCurrentItem({ ...currentItem, sponsors: e.target.value })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={sponsors.find(s => s.id === value)?.name} />
                      ))}
                    </Box>
                  )}
                >
                  {sponsors.map((sponsor) => (
                    <MenuItem key={sponsor.id} value={sponsor.id}>
                      {sponsor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel>Fish</InputLabel>
                <Select
                  multiple
                  value={currentItem.fish || []}
                  onChange={(e) => setCurrentItem({ ...currentItem, fish: e.target.value })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={fish.find(f => f.id === value)?.name} />
                      ))}
                    </Box>
                  )}
                >
                  {fish.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            // Fish Event form
            <>
              <TextField
                margin="dense"
                label="Date & Time"
                type="datetime-local"
                fullWidth
                value={currentItem.time_date || ''}
                onChange={(e) => setCurrentItem({ ...currentItem, time_date: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                margin="dense"
                label="Weight"
                type="number"
                fullWidth
                value={currentItem.weight || ''}
                onChange={(e) => setCurrentItem({ ...currentItem, weight: e.target.value })}
              />
              <FormControl fullWidth className={classes.formControl}>
                <Autocomplete
                  value={divisionFish.find(f => f.id === currentItem.fish) || null}
                  onChange={(event, newValue) => {
                    setCurrentItem({ ...currentItem, fish: newValue ? newValue.id : null });
                  }}
                  options={divisionFish}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => <TextField {...params} label="Fish" />}
                  filterOptions={(options, { inputValue }) => {
                    const filterValue = inputValue.toLowerCase().trim();
                    return options.filter(
                      (option) => option.name.toLowerCase().includes(filterValue)
                    );
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: '8px' }}>
                        ID: {option.id}
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </FormControl>
              <FormControl fullWidth className={classes.formControl}>
                <Autocomplete
                  value={fishermen.find(f => f.id === currentItem.fisherman) || null}
                  onChange={(event, newValue) => {
                    if (newValue && newValue.inputValue) {
                      // Handle the case when a new fisherman is added
                      handleAddNewFisherman(newValue.inputValue);
                    } else {
                      setCurrentItem({ ...currentItem, fisherman: newValue ? newValue.id : null });
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filterOptions(options, params);
                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === `${option.firstname} ${option.lastname}`);
                    if (inputValue !== '' && !isExisting) {
                      filtered.push({
                        inputValue,
                        firstname: inputValue.split(' ')[0] || '',
                        lastname: inputValue.split(' ')[1] || '',
                        id: `new-${inputValue}`,
                      });
                    }
                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  options={fishermen}
                  getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === 'string') {
                      return option;
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    // Regular option
                    return `${option.firstname} ${option.lastname}`;
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.inputValue ? `Add "${option.inputValue}"` : (
                        <>
                          <Typography variant="body1">{option.firstname} {option.lastname}</Typography>
                          <Typography variant="caption" color="textSecondary" style={{ marginLeft: '8px' }}>
                            ID: {option.id}
                          </Typography>
                        </>
                      )}
                    </li>
                  )}
                  freeSolo
                  renderInput={(params) => <TextField {...params} label="Fisherman" />}
                />
              </FormControl>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel>Leaderboard</InputLabel>
                <Select
                  value={currentItem.leaderboard || ''}
                  onChange={(e) => setCurrentItem({ ...currentItem, leaderboard: e.target.value })}
                >
                  {leaderboardResults.map((leaderboard) => (
                    <MenuItem key={leaderboard.id} value={leaderboard.id}>
                      {leaderboard.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogSave}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* Fisherman Dialog */}
      <Dialog open={fishermanDialogOpen} onClose={handleFishermanDialogClose}>
        <DialogTitle>Add Fisherman</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="First Name"
            fullWidth
            value={newFisherman.firstname}
            onChange={(e) => setNewFisherman({ ...newFisherman, firstname: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Last Name"
            fullWidth
            value={newFisherman.lastname}
            onChange={(e) => setNewFisherman({ ...newFisherman, lastname: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFishermanDialogClose}>Cancel</Button>
          <Button onClick={handleFishermanDialogSave}>Save</Button>
        </DialogActions>
      </Dialog>
      <TopThreeFisherman
        open={topThreeModalOpen}
        onClose={handleTopThreeModalClose}
        divisionId={selectedDivision?.id}
        selectedLeaderboard={selectedLeaderboard}
      />
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NFRLeaderboard;